// Phase 78ab — Pilot "Complete Tamil" deep-translate hook.
//
// Usage:
//   const tr = useDeepTranslate();
//   <p>{tr(supplier.name)}</p>
//
// `tr(text)` returns the original text immediately, but if Complete Tamil
// mode (`carloo_deep_tr=1` in localStorage) is active it asynchronously
// fetches the Tamil translation in the background and re-renders the
// component once the cache is populated. Repeat calls for the same string
// resolve from cache and never re-hit the backend.
import { useEffect, useState, useCallback, useRef } from 'react';
import api from './api';

const CACHE_PREFIX = 'carloo_tr_ta::';
// Pending strings collected across all hooks during one event-loop tick
// so we send ONE batch request to the backend per microtask.
const PENDING = new Set();
const SUBSCRIBERS = new Set();
let FLUSH_HANDLE = null;

function readCache(text) {
  return localStorage.getItem(CACHE_PREFIX + text);
}
function writeCache(text, translated) {
  try { localStorage.setItem(CACHE_PREFIX + text, translated); } catch {}
}

function isDeepActive() {
  return localStorage.getItem('carloo_deep_tr') === '1';
}

function notify() {
  SUBSCRIBERS.forEach(fn => { try { fn(); } catch {} });
}

async function flushBatch() {
  FLUSH_HANDLE = null;
  if (PENDING.size === 0) return;
  const batch = Array.from(PENDING);
  PENDING.clear();
  try {
    const { data } = await api.post('/translate/batch', { texts: batch, target: 'ta' });
    const translations = data?.translations || [];
    batch.forEach((src, i) => {
      const t = translations[i];
      if (typeof t === 'string') writeCache(src, t);
    });
    notify();
  } catch {
    // On failure, write the original back so we don't retry every render.
    batch.forEach(src => writeCache(src, src));
    notify();
  }
}

function scheduleFlush() {
  if (FLUSH_HANDLE) return;
  // Tiny delay so a screen-full of cards (≈30-50 strings) coalesces into
  // one HTTP request.
  FLUSH_HANDLE = setTimeout(flushBatch, 200);
}

function shouldTranslate(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (!trimmed) return false;
  // Skip strings without any Latin letters (already Tamil, or pure
  // numerals / symbols / phone numbers). The backend would also pass
  // them through but we save a round trip.
  return /[A-Za-z]/.test(trimmed);
}

export function useDeepTranslate() {
  const [, setTick] = useState(0);
  const activeRef = useRef(isDeepActive());

  // Re-render whenever Complete Tamil is toggled or a batch finishes.
  useEffect(() => {
    const bump = () => {
      activeRef.current = isDeepActive();
      setTick(x => x + 1);
    };
    SUBSCRIBERS.add(bump);
    window.addEventListener('carloo-lang-changed', bump);
    return () => {
      SUBSCRIBERS.delete(bump);
      window.removeEventListener('carloo-lang-changed', bump);
    };
  }, []);

  const tr = useCallback((text) => {
    if (!activeRef.current) return text;
    if (!shouldTranslate(text)) return text;
    const cached = readCache(text);
    if (cached !== null) return cached;
    if (!PENDING.has(text)) {
      PENDING.add(text);
      scheduleFlush();
    }
    return text; // show original until backend responds
  }, []);

  return tr;
}
